// src/layers/imagery/coverageBuckets.js
//
// Display-only bucketing for the series-level county coverage rollup.
// Pure functions, no React. Raw aoi_coverage_ratio values remain on the data
// — callers that want the precise number keep using fmtCoveragePct.

/** @typedef {import('./types').CountyCoverage} CountyCoverage */

/**
 * Coverage buckets. Boundaries here are display thresholds, not data
 * filters: every county still appears in the flyout regardless of bucket.
 *
 *   ratio >= FULL                 → "fully" — call it covered
 *   FULL > ratio >= PARTIAL       → "partial"
 *   PARTIAL > ratio >  0          → "minimal"
 *   ratio == 0 (or null)          → not counted in any bucket but still in total
 *
 * The 95% / 10% defaults match the user's mental model: "basically covered"
 * vs. "real but limited" vs. "edge of footprint".
 */
export const COVERAGE_THRESHOLDS = {
  FULL: 0.95,
  PARTIAL: 0.10,
};

/**
 * @param {CountyCoverage[]|null} counties
 * @returns {{ total: number, full: number, partial: number, minimal: number } | null}
 */
export function bucketCounties(counties) {
  if (!Array.isArray(counties)) return null;
  let full = 0, partial = 0, minimal = 0;
  for (const c of counties) {
    const r = c.aoi_coverage_ratio ?? 0;
    if (r >= COVERAGE_THRESHOLDS.FULL) full++;
    else if (r >= COVERAGE_THRESHOLDS.PARTIAL) partial++;
    else if (r > 0) minimal++;
  }
  return { total: counties.length, full, partial, minimal };
}

/**
 * Format the summary line for the collapsed series row.
 *   "20 counties · 4 full · 12 partial · 4 minimal"
 * Zero-count segments are omitted, so a series with 100% coverage across all
 * counties just reads "20 counties · 20 full".
 *
 * Returns null when buckets is null or total is 0, so the caller can fall
 * back to its no-coverage placeholder.
 */
export function formatCoverageSummary(buckets) {
  if (!buckets || buckets.total === 0) return null;
  const parts = [
    `${buckets.total} ${buckets.total === 1 ? "county" : "counties"}`,
  ];
  if (buckets.full > 0) parts.push(`${buckets.full} full`);
  if (buckets.partial > 0) parts.push(`${buckets.partial} partial`);
  if (buckets.minimal > 0) parts.push(`${buckets.minimal} minimal`);
  return parts.join(" · ");
}
